import { useCallback, useState } from 'react';
import { getWorkflowInstance } from '../services/workflowService';

const formatDate = (isoDate) => {
  if (!isoDate) return '-';

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '-';

  return `${String(date.getDate()).padStart(2, '0')}/${String(
    date.getMonth() + 1
  ).padStart(2, '0')}/${date.getFullYear()}`;
};

const resolveTaskTitle = (taskItem) =>
  taskItem?.nombreTarea ||
  taskItem?.titulo ||
  taskItem?.title ||
  taskItem?.task ||
  (taskItem?.id ? `Tarea #${taskItem.id}` : 'Tarea');

const resolveTaskPriority = (taskItem) => {
  const raw =
    taskItem?.priority || taskItem?.prioridad || taskItem?.priorityLabel;
  if (typeof raw === 'string' && raw.trim()) return raw;
  return 'Media';
};

export const useTaskWorkflowFlow = () => {
  const [workflowByTaskId, setWorkflowByTaskId] = useState({});
  const [selectedWorkflowFlow, setSelectedWorkflowFlow] = useState(null);
  const [selectedWorkflowRequest, setSelectedWorkflowRequest] = useState(null);
  const [isWorkflowFlowLoading, setIsWorkflowFlowLoading] = useState(false);
  const [workflowError, setWorkflowError] = useState(null);

  // Hacía una petición HTTP por tarea en pantalla para pintar un badge de workflow.
  // WorkflowInstances_SAAS está vacía, así que el mapa siempre quedaba {} y el coste
  // era una ráfaga de conexiones al pool de la API por cada listado. Para reactivarlo
  // hace falta un endpoint batch, no volver al N+1.
  const hydrateTaskWorkflows = useCallback(async () => {
    setWorkflowByTaskId({});
  }, []);

  const openTaskWorkflowFlow = useCallback(
    async (taskItem) => {
      const taskId = taskItem?.id;
      if (!taskId) return;

      const workflowSummary = workflowByTaskId[taskId];
      if (!workflowSummary?.id) return;

      setIsWorkflowFlowLoading(true);
      setWorkflowError(null);

      try {
        const detail = await getWorkflowInstance(workflowSummary.id);
        setSelectedWorkflowFlow(detail);
        setSelectedWorkflowRequest({
          task: resolveTaskTitle(taskItem),
          workflow:
            detail?.workflowName || workflowSummary?.workflowName || '-',
          requester:
            detail?.initiatedByName ||
            (detail?.initiatedByUserId
              ? `Usuario #${detail.initiatedByUserId}`
              : '-'),
          date: formatDate(detail?.startedAtUtc),
          priority: resolveTaskPriority(taskItem),
        });
      } catch (workflowLoadError) {
        console.error('Error al cargar detalle del workflow:', workflowLoadError);
        setWorkflowError(
          'No fue posible cargar el detalle del workflow para esta tarea.'
        );
        setSelectedWorkflowFlow(null);
        setSelectedWorkflowRequest(null);
      } finally {
        setIsWorkflowFlowLoading(false);
      }
    },
    [workflowByTaskId]
  );

  const closeTaskWorkflowFlow = useCallback(() => {
    setSelectedWorkflowFlow(null);
    setSelectedWorkflowRequest(null);
  }, []);

  return {
    workflowByTaskId,
    workflowError,
    isWorkflowFlowLoading,
    selectedWorkflowFlow,
    selectedWorkflowRequest,
    hydrateTaskWorkflows,
    openTaskWorkflowFlow,
    closeTaskWorkflowFlow,
    clearWorkflowError: () => setWorkflowError(null),
  };
};

export default useTaskWorkflowFlow;
